<template>
  <div class="configuration-page">
    <div class="configuration-tabs bg-white radius-7 mb-20">
      <div class="configuration-tabs__list" role="tablist" aria-label="Configuracoes">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="configuration-tabs__button"
          :class="{ active: activeTab === tab.key }"
          role="tab"
          :aria-selected="activeTab === tab.key"
          :aria-controls="`configuration-tab-${tab.key}`"
          @click="setActiveTab(tab.key)"
        >
          <i :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
        </button>
      </div>
    </div>

    <section
      v-show="activeTab === 'whatsapp'"
      id="configuration-tab-whatsapp"
      role="tabpanel"
      class="configuration-tab-panel"
    >
      <div class="row">
        <div class="col-12">
          <WhatsappConfigurations :disabled-by-plan="isAttendantUnavailable" />
        </div>
        <div class="col-12">
          <AdvancedInitialWhatsappMessageConfig v-if="isAdvancedAttendant" />
          <InitialWhatsappMessageConfig v-else :disabled-by-plan="isAttendantUnavailable" />
        </div>
        <div class="col-12">
          <UraTreeView :disabled-by-plan="isAttendantUnavailable" />
        </div>
      </div>
    </section>

    <section
      v-show="activeTab === 'goals'"
      id="configuration-tab-goals"
      role="tabpanel"
      class="configuration-tab-panel"
    >
      <GoalsList />
    </section>

    <section
      v-show="activeTab === 'hours'"
      id="configuration-tab-hours"
      role="tabpanel"
      class="configuration-tab-panel"
    >
      <div class="row">
        <div class="col-xxl-7 col-xl-9">
          <OperatingHoursConfig />
        </div>
      </div>
    </section>

    <section
      v-show="activeTab === 'users'"
      id="configuration-tab-users"
      role="tabpanel"
      class="configuration-tab-panel"
    >
      <UsersList />
    </section>

    <section
      v-show="activeTab === 'services'"
      id="configuration-tab-services"
      role="tabpanel"
      class="configuration-tab-panel"
    >
      <CategoriesList />
      <SubcategoriesList />
      <ServicesList />
    </section>

    <section
      v-show="activeTab === 'payments'"
      id="configuration-tab-payments"
      role="tabpanel"
      class="configuration-tab-panel"
    >
      <PaymentMethodsList />
    </section>
  </div>
</template>

<script lang="ts">

import { defineComponent } from "vue";
import WhatsappConfigurations from "../../components/Configuration/WhatsappConfigurations.vue";
import OperatingHoursConfig from "../../components/Configuration/OperatingHoursConfig.vue";
import InitialWhatsappMessageConfig from "../../components/Configuration/InitialWhatsappMessageConfig.vue";
import AdvancedInitialWhatsappMessageConfig from "../../components/Configuration/AdvancedInitialWhatsappMessageConfig.vue";
import UraTreeView from "../../components/Configuration/UraTreeView.vue";
import GoalsList from "../../components/Users/GoalsList.vue";
import UsersList from "../../components/Users/UsersList.vue";
import ServicesList from "../../components/Services/ServicesList.vue";
import CategoriesList from "../../components/Categories/CategoriesList.vue";
import SubcategoriesList from "../../components/Subcategories/SubcategoriesList.vue";
import PaymentMethodsList from "../../components/Payment/PaymentMethodsList.vue";
import { mapActions, mapGetters } from 'vuex';

const CONFIGURATION_ACTIVE_TAB_KEY = "configuration.activeTab";

export default defineComponent({
  name: "ConfigurationPage",
  components: {
    WhatsappConfigurations,
    InitialWhatsappMessageConfig,
    AdvancedInitialWhatsappMessageConfig,
    OperatingHoursConfig,
    UraTreeView,
    UsersList,
    GoalsList,
    ServicesList,
    CategoriesList,
    SubcategoriesList,
    PaymentMethodsList
  },
  data() {
    return {
      activeTab: "whatsapp",
      tabs: [
        { key: "services", label: "Serviços", icon: "ri-scissors-line" },
        { key: "users", label: "Usuários", icon: "ri-user-settings-line" },
        { key: "hours", label: "Horários", icon: "ri-time-line" },
        { key: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-line" },
        { key: "goals", label: "Metas", icon: "ri-line-chart-line" },
        { key: "payments", label: "Pagamentos", icon: "ri-bank-card-line" },
      ],
    };
  },
  methods: {
    ...mapActions([
      "setAllUsersList",
      "setRolesList",
      "fetchServiceList",
      "fetchCategories",
      "fetchAllPaymentMethods",
      "fetchStatusWhatsapp"
    ]),
    setActiveTab(tabKey: string) {
      this.activeTab = tabKey;
      localStorage.setItem(CONFIGURATION_ACTIVE_TAB_KEY, tabKey);
    },
  },
  computed: {
    ...mapGetters([
      "getConfigurations",
    ]),
    isAttendantUnavailable(): boolean {
      const attendantType = this.getConfigurations?.type_of_attendant;
      return attendantType === 0 || attendantType === "0";
    },
    isAdvancedAttendant(): boolean {
      return Number(this.getConfigurations?.type_of_attendant) === 2;
    },
  },
  mounted() {
    const storedTab = localStorage.getItem(CONFIGURATION_ACTIVE_TAB_KEY);
    if (storedTab && this.tabs.some((tab) => tab.key === storedTab)) {
      this.activeTab = storedTab;
    }

    this.setAllUsersList(); 
    this.fetchCategories();
    this.fetchAllPaymentMethods();
    if (!this.isAttendantUnavailable) {
      this.fetchStatusWhatsapp();
    }
  },
});
</script>

<style scoped>
.configuration-page {
  min-width: 0;
}

.configuration-tabs {
  overflow-x: auto;
  padding: 8px;
}

.configuration-tabs__list {
  display: flex;
  gap: 6px;
  min-width: max-content;
}

.configuration-tabs__button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--splash-muted-color);
  display: inline-flex;
  font-size: 14px;
  font-weight: 700;
  gap: 8px;
  min-height: 42px;
  padding: 0 16px;
  transition: var(--transition);
  white-space: nowrap;
}

.configuration-tabs__button i {
  font-size: 18px;
  line-height: 1;
}

.configuration-tabs__button:hover,
.configuration-tabs__button.active {
  background: rgba(101, 96, 240, 0.1);
  color: var(--splash-primary-color);
}

.configuration-tab-panel {
  min-width: 0;
}

@media (max-width: 767px) {
  .configuration-tabs {
    margin-left: -4px;
    margin-right: -4px;
  }

  .configuration-tabs__button {
    font-size: 13px;
    min-height: 40px;
    padding: 0 12px;
  }
}
</style>

<style>
body.dark .configuration-tabs__button:hover,
body.dark .configuration-tabs__button.active {
  background: rgba(167, 164, 255, 0.16);
  color: #a7a4ff;
}
</style>
